import type {
  OrderDashboardReport,
  PartnerMetric,
} from '@/api/hooks/useOrderDashboard'
import { formatCount } from '@/utils/stringFormatters'
import { GraviGrid } from '@gravitate-js/excalibrr'
import type {
  ColDef,
  RowClassParams,
  RowClickedEvent,
  ValueFormatterParams,
} from 'ag-grid-community'
import { Card } from 'antd'
import { useMemo } from 'react'
import { PercentText } from './PercentText'
import { metricRowClass, useMetricRowHighlight } from './metricRow'
import { sortPartnerMetrics } from './metrics'

/**
 * Shows roughly ten partners before it scrolls, so a tenant with a long
 * partner list cannot push the cards below it off the page.
 */
const ROW_HEIGHT = 32
const HEADER_HEIGHT = 32
const ROWS_BEFORE_SCROLL = 10
const GRID_HEIGHT = HEADER_HEIGHT + ROWS_BEFORE_SCROLL * ROW_HEIGHT

interface PartnerMetricsCardProps {
  report: OrderDashboardReport | undefined
  loading: boolean
  /** tenant_id of the partner the issue list is filtered to, if any. */
  selectedTenantId: string | null
  /** Picks a partner to filter the issues and the event metrics by; null
   * clears the filter. */
  onSelectPartner: (partner: PartnerMetric | null) => void
}

const countFormatter = (params: ValueFormatterParams<PartnerMetric>) =>
  formatCount(params.value)

// Every column carries a field or colId: without one GraviGrid cannot match a
// column to its saved config, and they reshuffle as soon as one is added.
const columnDefs: ColDef<PartnerMetric>[] = [
  {
    headerName: 'PARTNER',
    field: 'tenant_name',
    flex: 1.4,
    minWidth: 120,
  },
  {
    headerName: 'HIGH EVENTS %',
    colId: 'high_events_percentage',
    field: 'high_events_percentage',
    type: 'numericColumn',
    flex: 1,
    minWidth: 110,
    cellRenderer: (params: { value: number | null }) => (
      <PercentText value={params.value} />
    ),
  },
  {
    headerName: 'ALL EVENTS %',
    colId: 'all_events_percentage',
    field: 'all_events_percentage',
    type: 'numericColumn',
    flex: 1,
    minWidth: 110,
    cellRenderer: (params: { value: number | null }) => (
      <PercentText value={params.value} />
    ),
  },
  {
    headerName: 'ORDERS',
    field: 'orders_total',
    type: 'numericColumn',
    flex: 0.8,
    minWidth: 80,
    valueFormatter: countFormatter,
  },
  {
    headerName: 'FAILED (HIGH)',
    field: 'orders_failed',
    type: 'numericColumn',
    flex: 1,
    minWidth: 100,
    valueFormatter: countFormatter,
  },
]

/** One row per partner, each already totalled over the window. */
export function PartnerMetricsCard({
  report,
  loading,
  selectedTenantId,
  onSelectPartner,
}: PartnerMetricsCardProps) {
  const rows = useMemo(() => sortPartnerMetrics(report), [report])

  const gridApiRef = useMetricRowHighlight(selectedTenantId)

  // Clicking the row the list is already filtered to takes the filter back
  // off, matching how a category row inside a count card behaves.
  const selectRow = (event: RowClickedEvent<PartnerMetric>) => {
    const partner = event.data
    if (!partner) return
    onSelectPartner(partner.tenant_id === selectedTenantId ? null : partner)
  }

  return (
    <Card size='small' title='Partners' loading={loading}>
      <div
        className='dashboard-metrics-grid is-clickable'
        style={{ height: GRID_HEIGHT }}
      >
        <GraviGrid
          rowData={rows}
          columnDefs={columnDefs}
          storageKey='orders-dashboard-partners-v1'
          sideBar={false}
          externalRef={gridApiRef}
          agPropOverrides={{
            getRowId: (params) => params.data.tenant_id,
            onRowClicked: selectRow,
            getRowClass: (params: RowClassParams<PartnerMetric>) =>
              metricRowClass(params.data?.tenant_id === selectedTenantId),
            suppressRowClickSelection: true,
            rowGroupPanelShow: 'never',
            headerHeight: HEADER_HEIGHT,
            rowHeight: ROW_HEIGHT,
            defaultColDef: {
              sortable: true,
              resizable: true,
              suppressMovable: true,
            },
            overlayNoRowsTemplate:
              '<span style="color: var(--muted-foreground); font-size: 13px;">No partner activity in this window</span>',
          }}
        />
      </div>
    </Card>
  )
}
